import * as React from 'react';
import { useState } from 'react';
import {
  Button,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Box,
  Typography,
  Snackbar,
  createTheme,
  ThemeProvider,
  InputAdornment,
  IconButton,
  Fade,
  Grid
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/system';
import axios from 'axios';
import User from '../data/User';
import { userSchema } from '../data/UserValidation';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import signUpBg from '../assets/signup.JPG';
import API_URL from '../apiConfig';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#e88ba9',
    },
    text: {
      primary: '#5c434a',
      secondary: '#8c7077',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h3: {
      fontFamily: '"Playfair Display", serif',
      color: '#d46a89',
    }
  },
});

const BakeryButton = styled(Button)({
  background: '#e88ba9',
  borderRadius: '30px',
  border: 0,
  color: 'white',
  height: 54,
  padding: '0 30px',
  boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  marginTop: '32px',
  marginBottom: '24px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)',
    background: '#d46a89',
  },
  '&:active': {
    transform: 'translateY(0)',
  }
});

const SoftTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    color: '#5c434a',
    backdropFilter: 'blur(10px)',
    '& fieldset': {
      borderColor: 'rgba(232, 139, 169, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: '#e88ba9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#e88ba9',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#8c7077',
    '&.Mui-focused': {
      color: '#e88ba9',
    }
  },
  '& .MuiSvgIcon-root': {
    color: '#8c7077',
  }
});

const SignUp = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    fullname: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
  });

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let formdata = {
      name: form.fullname,
      email: form.email,
      password: form.password,
      phone: form.phone,
      address: form.address,
    };
    try {
      await userSchema.validate(formdata);
    } catch (err) {
      setErrorMessage(err.message);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setErrorMessage("Passwords do not match!");
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, formdata);
      if (response.status === 201) {
        User.instance.setToken(response.data.token);
        navigate('/profile');
      } else {
        setErrorMessage("Failed to register: " + response.data.message);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error registering. Please try again.');
    }
  };

  const handleclose = (e, reason) => {
    if (reason === 'clickaway') return;
    setErrorMessage("");
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          paddingX: 2,
          pt: '100px',
          pb: '60px',
        }}
      >
        <CssBaseline />

        {/* Background Image Layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("${signUpBg}")`,
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(3px)', // Blurred cake image 
            zIndex: -2
          }}
        />

        {/* White Gradiant Overlay Layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.95) 100%)', // White smoke
            zIndex: -1
          }}
        />

        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              p: { xs: 4, md: 5 },
              width: '100%',
              maxWidth: '650px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 1
            }}
          >
            <Typography component="h1" variant="h3" sx={{ fontWeight: 800, mb: 1, textAlign: 'center' }}>
              Join the Bakery
            </Typography>
            <Typography variant="body1" sx={{ color: '#8c7077', mb: 4, fontWeight: 500, textAlign: 'center' }}>
              Create an account to start ordering delicious, freshly baked cakes.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <SoftTextField
                    required
                    fullWidth
                    id="fullname"
                    label="Full Name"
                    name="fullname"
                    autoComplete="name"
                    autoFocus
                    value={form.fullname}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SoftTextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SoftTextField
                    required
                    fullWidth
                    name="phone"
                    label="Contact No"
                    type="text"
                    id="phone"
                    value={form.phone}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <SoftTextField
                    required
                    fullWidth
                    name="address"
                    label="Delivery Address"
                    type="text"
                    id="address"
                    value={form.address}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SoftTextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: '#8c7077' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SoftTextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, mb: 1, display: 'flex', justifyContent: 'center' }}>
                <FormControlLabel
                  control={<Checkbox value="allowExtraEmails" sx={{ color: '#8c7077', '&.Mui-checked': { color: '#e88ba9' } }} />}
                  label={<Typography sx={{ color: '#8c7077', fontSize: '0.9rem', fontWeight: 600 }}>I want to receive sweet deals via email</Typography>}
                />
              </Box>

              <BakeryButton
                type="submit"
                fullWidth
              >
                Create Account
              </BakeryButton>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#8c7077', fontWeight: 500 }}>
                  Already part of our family?{' '}
                  <Link component={RouterLink} to="/login" sx={{ color: '#e88ba9', fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    Sign in directly
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>

      <Snackbar
        message={errorMessage}
        autoHideDuration={4000}
        open={errorMessage !== ''}
        onClose={handleclose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        ContentProps={{
          sx: { background: '#fff', color: '#d46a89', fontWeight: 'bold', border: '1px solid #f4a2bc', boxShadow: '0 10px 30px rgba(212, 106, 137, 0.3)' }
        }}
      />
    </ThemeProvider>
  );
}

export default SignUp;
